import type { BaseClient } from '../../http/BaseClient.js';
import { unwrapData } from '../../common/envelope.js';
import type {
  BuySubscriptionRequest,
  BuySubscriptionResponse,
  CancelSubscriptionResponse,
  ListPaymentsResponse,
  OrgSubscription,
  Payment,
  PreviewChangeRequest,
  PreviewChangeResponse,
  Subscription,
} from './types.js';

export class SubscriptionsClient {
  constructor(private readonly http: BaseClient) {}

  async listCatalog(): Promise<Subscription[]> {
    const res = await this.http.get<{ success: boolean; data: Subscription[] }>('/subscriptions', { skipAuth: true });
    return res.data ?? [];
  }

  async getMine(): Promise<OrgSubscription | null> {
    const res = await this.http.get<{ success: boolean; data: OrgSubscription | null }>('/subscriptions/my');
    return res.data ?? null;
  }

  buy(body: BuySubscriptionRequest): Promise<BuySubscriptionResponse> {
    return this.http.post<BuySubscriptionResponse>('/subscriptions/buy', { body });
  }

  /**
   * Preview the cost/credit impact of a plan change (pro-rated charge,
   * credit delta, effective dates) for the confirmation modal — no change
   * is made. kind="new" means a first purchase that would go via Checkout.
   */
  previewChange(body: PreviewChangeRequest): Promise<PreviewChangeResponse> {
    return this.http.post<PreviewChangeResponse>('/subscriptions/preview-change', { body });
  }

  cancel(): Promise<CancelSubscriptionResponse> {
    return this.http.post<CancelSubscriptionResponse>('/subscriptions/cancel', {});
  }

  async listPayments(): Promise<Payment[]> {
    const res = await this.http.get<ListPaymentsResponse>('/subscriptions/payments');
    return res.data ?? [];
  }

  /** Stripe-hosted billing portal URL. */
  async portal(): Promise<{ url: string }> {
    const res = await this.http.post<{ success: boolean; data: { url: string } }>('/subscriptions/portal', {});
    return unwrapData(res);
  }
}
